// Guide-step advance affordance (Tier 1.c § 4.5).
//
// While `controller.guideStepPending` is true (set after the assistant
// finishes a Guide-mode turn), this chip renders two buttons:
//
//   [ ✓ Done ]                  invokes controller.continueGuideStep(),
//                               which clears guideStepPending and sends
//                               the canned "what should I do next?" turn
//                               with a fresh screenshot. A "✓ Step done"
//                               system breadcrumb is pushed into the
//                               thread. The continuation prompt quotes
//                               the just-acknowledged annotation back at
//                               the model so single-shot asks
//                               (where there is no next step) don't loop
//                               on redrawing the same target.
//
//   [ Stop ]                    clears guideStepPending and the
//                               persistent annotation; ends the walkthrough.
//                               No breadcrumb (per spec § 4.5).
//
// (The previous "Show again" button is gone — annotations now persist
// until Done/Stop/new task, so there's nothing to redraw.)
//
// Mounted inside Thread.jsx's composer-layer (above the composer). Returns
// null when guideStepPending is false. While visible it publishes its
// height as --copilot-guide-advance-h so the thread viewport reserves
// padding-bottom and the last bubble stays in view.

import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { clearAnnotations } from "../annotationOverlay";

export const GuideAdvanceChip = observer(function GuideAdvanceChip({ controller }) {
  const rootRef = useRef(null);

  // Publish height as --copilot-guide-advance-h on the closest .copilot-root
  // so the thread viewport reserves space above the composer for this chip.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const panel = el.closest(".copilot-root");
    if (!panel) return undefined;
    let lastH = -1;
    let rafId = 0;
    const update = () => {
      const h = el.offsetHeight;
      if (h === lastH) return;
      lastH = h;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        panel.style.setProperty("--copilot-guide-advance-h", `${h}px`);
        rafId = 0;
      });
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      panel.style.setProperty("--copilot-guide-advance-h", "0px");
    };
  }, [controller.guideStepPending]);

  if (!controller.guideStepPending) return null;

  const stop = () => {
    runInAction(() => {
      controller.guideStepPending = false;
      // Spec § 4.5: no breadcrumb on Stop — the walkthrough simply ends.
    });
    // User said stop; clear the persistent annotation. (Overlay review item 2.)
    clearAnnotations();
  };

  const done = () => {
    runInAction(() => {
      // Spec § 4.5 breadcrumb so the user can see what happened when
      // scrolling back later. continueGuideStep() clears
      // guideStepPending and pushes the canned continuation user-message.
      controller.messages.push({
        role: "system",
        text: "✓ Step done — co-pilot is checking your screen",
      });
    });
    controller.continueGuideStep();
  };

  return (
    <div
      ref={rootRef}
      className="copilot-guide-advance"
      role="group"
      aria-label="Guide step advance"
    >
      <button
        type="button"
        className="copilot-guide-advance__btn copilot-guide-advance__btn--primary"
        onClick={done}
      >
        ✓ Done
      </button>
      <button
        type="button"
        className="copilot-guide-advance__btn"
        onClick={stop}
      >
        Stop
      </button>
    </div>
  );
});
